import { useMemo } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { ArrowLeft, Clock, ChefHat, Users, Lightbulb, Printer } from 'lucide-react';
import Navbar from '@/components/site/Navbar';
import Footer from '@/components/site/Footer';
import Seo from '@/components/Seo';
import RecipePairings from '@/components/site/RecipePairings';
import { findRecipeBySlug, RECIPES } from '@/content/recipes';
import { trackEvent } from '@/lib/analytics';

const SITE = 'https://themeatworks.com.au';

/**
 * Full recipe article — Google Recipe rich-result eligible.
 * Emits a canonical schema.org/Recipe JSON-LD payload plus BreadcrumbList
 * for SERP breadcrumb display. Text uses simple markup so the important
 * signals (ingredient list, HowToStep instructions, times) survive.
 */
function isoDuration(minutes) {
  if (!minutes) return undefined;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `PT${h ? h + 'H' : ''}${m ? m + 'M' : ''}` || undefined;
}

export default function RecipeArticle() {
  const { slug } = useParams();
  const recipe = findRecipeBySlug(slug);

  const related = useMemo(() => {
    if (!recipe) return [];
    return RECIPES
      .filter((r) => r.slug !== slug && (r.category === recipe.category || r.cuisine === recipe.cuisine))
      .slice(0, 3);
  }, [recipe, slug]);

  if (!recipe) return <Navigate to="/recipes" replace />;

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Recipe',
    name: recipe.title,
    description: recipe.description,
    image: [SITE + recipe.heroImage],
    author: { '@type': 'Organization', name: 'The Meat Works', url: SITE },
    datePublished: recipe.dateISO,
    prepTime: isoDuration(recipe.prepMinutes),
    cookTime: isoDuration(recipe.cookMinutes),
    totalTime: isoDuration(recipe.prepMinutes + recipe.cookMinutes),
    recipeCategory: recipe.category,
    recipeCuisine: recipe.cuisine,
    recipeYield: recipe.yield,
    keywords: recipe.keywords,
    recipeIngredient: recipe.ingredients.filter((i) => !i.endsWith(':')),
    recipeInstructions: recipe.instructions.map((s) => ({
      '@type': 'HowToStep', name: s.name, text: s.text,
    })),
  };

  const breadcrumb = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE}/` },
      { '@type': 'ListItem', position: 2, name: 'Recipes', item: `${SITE}/recipes` },
      { '@type': 'ListItem', position: 3, name: recipe.title, item: `${SITE}/recipes/${recipe.slug}` },
    ],
  };

  const totalMinutes = recipe.prepMinutes + recipe.cookMinutes;

  return (
    <div className="section-light App relative">
      <Seo
        title={recipe.title}
        description={recipe.description}
        keywords={recipe.keywords}
        path={`/recipes/${recipe.slug}`}
        image={SITE + recipe.heroImage}
        jsonLd={[jsonLd, breadcrumb]}
      />
      <div className="no-print"><Navbar /></div>

      {/* Hero */}
      <section className="pt-24 sm:pt-32 pb-16 bg-gradient-to-b from-[#FDFBF7] to-[#FAFAFA] no-print">
        <div className="max-w-4xl mx-auto px-6 sm:px-10">
          <Link
            to="/recipes"
            data-testid="recipe-back"
            className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.28em] text-black/50 hover:text-black transition-colors mb-6"
          >
            <ArrowLeft size={12} /> All recipes
          </Link>
          <div className="text-[10px] uppercase tracking-[0.32em] text-[var(--mw-primary)] mb-3">
            {recipe.cuisine} &middot; {recipe.category}
          </div>
          <h1 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-black">
            {recipe.title}
          </h1>
          <p className="mt-6 max-w-3xl text-lg text-black/70 leading-relaxed">{recipe.tagline}</p>

          <div className="mt-8 flex flex-wrap items-center gap-6 text-[11px] uppercase tracking-[0.22em] text-black/55">
            <span className="inline-flex items-center gap-2"><Clock size={14} className="text-[var(--mw-primary)]" /> {totalMinutes >= 60 ? `${Math.floor(totalMinutes/60)}h ${totalMinutes%60}m` : `${totalMinutes}m`} total</span>
            <span className="inline-flex items-center gap-2"><Users size={14} className="text-[var(--mw-primary)]" /> {recipe.yield}</span>
            <span className="inline-flex items-center gap-2"><ChefHat size={14} className="text-[var(--mw-primary)]" /> {recipe.difficulty}</span>
            <button
              type="button"
              onClick={() => { trackEvent('Recipe Printed', { recipe: recipe.slug }); window.print(); }}
              data-testid="recipe-print"
              title="Print a clean kitchen-friendly card of this recipe"
              className="ml-auto inline-flex items-center gap-2 border border-black/20 hover:border-black text-black px-4 py-2 tracking-[0.22em] transition-colors"
            >
              <Printer size={14} /> Print recipe
            </button>
          </div>
        </div>
      </section>

      {/* Body */}
      <main className="bg-[#FAFAFA] pb-24">
        <div className="max-w-4xl mx-auto px-6 sm:px-10 recipe-print-card">
          {/* Print-only header (screen shows the hero section above) */}
          <div className="hidden print:block mb-6">
            <div style={{ fontSize: '10pt', letterSpacing: '0.32em', textTransform: 'uppercase', color: '#9B111E', marginBottom: '4pt' }}>
              {recipe.cuisine} · {recipe.category}
            </div>
            <h1 style={{ font: '400 26pt/1.1 Georgia, serif', margin: '0 0 6pt' }}>{recipe.title}</h1>
            <p style={{ font: '11pt/1.5 Georgia, serif', color: '#333', margin: '0 0 10pt' }}>{recipe.tagline}</p>
            <div style={{ display: 'flex', gap: '18pt', fontSize: '9pt', textTransform: 'uppercase', letterSpacing: '0.18em', color: '#555' }}>
              <span>Prep · {recipe.prepMinutes}m</span>
              <span>Cook · {recipe.cookMinutes}m</span>
              <span>Total · {totalMinutes >= 60 ? `${Math.floor(totalMinutes/60)}h ${totalMinutes%60}m` : `${totalMinutes}m`}</span>
              <span>Serves · {recipe.yield}</span>
              <span>{recipe.difficulty}</span>
            </div>
          </div>

          {/* Hero image — screen only, keeps the printed card clean */}
          <div className="relative aspect-[16/10] mb-14 overflow-hidden bg-black/5 border border-black/[0.06] no-print">
            <img
              src={recipe.heroImage}
              alt={recipe.title}
              width="1024"
              height="640"
              fetchpriority="high"
              decoding="async"
              className="absolute inset-0 w-full h-full object-cover"
            />
          </div>

          {/* Description */}
          <p className="text-lg text-black/80 leading-relaxed max-w-2xl mb-14 print:text-base print:mb-6">{recipe.description}</p>

          {/* Grid — ingredients + instructions */}
          <div className="grid lg:grid-cols-[1fr_1.4fr] gap-14 print-grid">
            {/* Ingredients */}
            <aside className="lg:sticky lg:top-32 lg:self-start">
              <div className="text-[10px] uppercase tracking-[0.32em] text-[var(--mw-primary)] mb-4">Ingredients</div>
              <h2 className="font-display text-2xl text-black mb-6">You&apos;ll need</h2>
              <ul className="space-y-2.5">
                {recipe.ingredients.map((ing, i) => (
                  <li
                    key={i}
                    className={ing.endsWith(':')
                      ? 'pt-4 text-[10px] uppercase tracking-[0.28em] text-[var(--mw-primary)]'
                      : 'flex items-start gap-3 text-black/75 leading-relaxed'
                    }
                  >
                    {!ing.endsWith(':') && (
                      <span className="mt-2 w-1 h-1 rounded-full bg-[var(--mw-primary)] shrink-0" />
                    )}
                    {ing}
                  </li>
                ))}
              </ul>
            </aside>

            {/* Instructions */}
            <div>
              <div className="text-[10px] uppercase tracking-[0.32em] text-[var(--mw-primary)] mb-4">Method</div>
              <h2 className="font-display text-2xl text-black mb-8">Cook it right</h2>
              <ol className="space-y-8">
                {recipe.instructions.map((step, i) => (
                  <li key={i} className="grid grid-cols-[auto_1fr] gap-5">
                    <div className="text-[var(--mw-primary)] font-display text-3xl leading-none">{String(i + 1).padStart(2, '0')}</div>
                    <div>
                      <h3 className="font-display text-lg text-black mb-2">{step.name}</h3>
                      <p className="text-black/70 leading-relaxed">{step.text}</p>
                    </div>
                  </li>
                ))}
              </ol>

              {/* Tips */}
              {recipe.tips?.length > 0 && (
                <div className="mt-14 p-6 bg-white border border-black/[0.08]">
                  <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] text-[var(--mw-primary)] mb-3">
                    <Lightbulb size={14} /> Chef&apos;s tips
                  </div>
                  <ul className="space-y-3 text-black/70 leading-relaxed">
                    {recipe.tips.map((tip, i) => <li key={i}>{tip}</li>)}
                  </ul>
                </div>
              )}

              {/* Buy-the-cuts pairings — moved out of the ingredients column
                  and placed as its own full-width section below (see JSX after
                  the print footer). Keeps the article body clean. */}
            </div>
          </div>

          {/* Print-only footer strip (screen version comes from the site Footer) */}
          <div className="hidden print:block recipe-print-footer">
            The Meat Works · Halal-certified · Malaga WA · themeatworks.com.au
          </div>

          {/* Cook this recipe — one-tap add-to-cart for the star cut + 2 pairings */}
          {recipe.productSlug && (
            <RecipePairings
              primarySlug={recipe.productSlug}
              pairingSlugs={recipe.pairingSlugs || []}
              recipeTitle={recipe.title}
            />
          )}

          {/* Related */}
          {related.length > 0 && (
            <div className="mt-24 pt-14 border-t border-black/[0.08] no-print">
              <h2 className="font-display text-2xl text-black mb-8">More {recipe.cuisine.toLowerCase()} recipes</h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                {related.map((r) => (
                  <Link
                    key={r.slug}
                    to={`/recipes/${r.slug}`}
                    className="group block"
                  >
                    <div className="relative aspect-[4/5] overflow-hidden bg-black/5 mb-3">
                      <img src={r.heroImage} alt={r.title} loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-700" />
                    </div>
                    <div className="text-[9px] uppercase tracking-[0.24em] text-[var(--mw-primary)] mb-1">{r.cuisine}</div>
                    <h3 className="font-display text-lg text-black leading-tight group-hover:text-[var(--mw-primary)] transition-colors">{r.title}</h3>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </main>

      <div className="no-print"><Footer /></div>
    </div>
  );
}
